import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  FileText,
  Folder,
  Image as ImageIcon,
  Inbox,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  Navigation as NavigationIcon,
  Newspaper,
  Package,
  Settings,
  ShieldCheck,
  X,
} from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { useAdminAuth } from "@/admin/AdminAuth";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutGrid, exact: true },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/services", label: "Services", icon: ShieldCheck },
  { to: "/admin/industries", label: "Industries", icon: Package },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/news", label: "News", icon: Newspaper },
  { to: "/admin/projects", label: "Projects", icon: Folder },
  { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
  { to: "/admin/navigation", label: "Navigation", icon: NavigationIcon },
  { to: "/admin/footer", label: "Footer", icon: Settings },
  { to: "/admin/site-settings", label: "Site Settings", icon: Settings },
  { to: "/admin/contact-submissions", label: "Inbox", icon: Inbox, badgeKey: "contact_new" },
  { to: "/admin/quote-requests", label: "Quote Requests", icon: Inbox, badgeKey: "quotes_new" },
];

export default function AdminLayout({ children, title }) {
  const { me, logout } = useAdminAuth();
  const [summary, setSummary] = useState({});
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    adminApi.get("/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-mist-off text-mist-ink font-sans flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-mist-navy text-white z-40 transform transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-brand">
            <img src="/logo-white.png" alt="MIST" className="h-7" />
            <span className="text-xs uppercase tracking-[0.22em] text-mist-gold font-semibold">CMS</span>
          </Link>
          <button className="lg:hidden text-white" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="p-3 flex-1 overflow-y-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            const badge = n.badgeKey ? summary[n.badgeKey] : 0;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.exact}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition-colors ${
                    isActive ? "bg-white/10 text-mist-gold" : "text-white/80 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span className="flex-1">{n.label}</span>
                {badge > 0 && (
                  <span className="text-[10px] rounded-full bg-mist-gold text-mist-navy font-semibold px-2 py-0.5">
                    {badge}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          {me && (
            <div className="text-[11px] text-white/60 mb-2 px-3">
              {me.email}
            </div>
          )}
          <NavLink
            to="/admin/change-password"
            data-testid="admin-nav-change-password"
            className="block px-3 py-2 text-[12px] text-white/70 hover:text-white rounded-md hover:bg-white/5"
          >
            Change password
          </NavLink>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-[12px] text-white/70 hover:text-white rounded-md hover:bg-white/5"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:ml-0">
        <header className="bg-white border-b border-mist-soft px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <button
            className="lg:hidden text-mist-navy"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            data-testid="admin-menu-open"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
          <h1 className="font-display text-xl text-mist-navy">{title}</h1>
          <div />
        </header>
        <main className="p-6 lg:p-8 max-w-6xl">{children}</main>
      </div>
    </div>
  );
}
